import { makeAutoObservable, toJS } from "mobx";

import { getMarkets } from "@/shared/api/endpoints";
import { MarketType, ExchangeType, MarketDTO } from "@/shared/api/endpoints/parties/types";
import { Mapper, Meta } from "@/shared/types/common-types";
import { joinMarket, joinPair } from "@/shared/utils/joins";
import { logError } from "@/shared/utils/logger";

export interface MarketDataType {
	party: string;
	partyUUID: string;
	marketUUID: string;
	marketType: MarketType;
	exchange: string;
	exchangeType: ExchangeType;
	base: string;
	quote: string;
	pairContract: string | null;
	network: string | null;
	pair: string;
	marketLabel: string;
	relations: {
		accounts: string[] | null;
		wallets: string[] | null;
		vaults: string[] | null;
		executors: string[] | null;
	};
	settings: {
		minExchangeAmount: string;
		amountDecimals: number;
		priceDecimals: number;
		buyFees: string | null;
		sellFees: string | null;
		transferFees: string | null;
		sleepage: string | null;
		gasMax: string | null;
	};
	meta: Meta | null;
}

export const marketDTOMapper: Mapper<MarketDTO, MarketDataType> = ({
	party,
	party_uuid,
	market_uuid,
	market_type,
	exchange,
	exchange_type,
	network,
	base,
	quote,
	pair_contract,
	relations,
	settings,
	meta,
}) => ({
	party,
	partyUUID: party_uuid,
	marketUUID: market_uuid,
	marketType: market_type,
	base,
	quote,
	pair: joinPair({ quote, base }),
	marketLabel: joinMarket({ quote, base, exchange }),
	exchange,
	exchangeType: exchange_type,
	network,
	pairContract: pair_contract,
	relations,
	settings: {
		minExchangeAmount: settings.min_exchange_amount,
		amountDecimals: settings.amount_decimal,
		priceDecimals: settings.price_decimal,
		buyFees: settings.buy_fees,
		sellFees: settings.sell_fees,
		transferFees: settings.transfer_fees,
		sleepage: settings.sleepage,
		gasMax: settings.gas_max,
	},
	meta,
});

export class MarketsStore {
	private _marketsMap: Record<string, MarketDataType[]> = {};

	constructor() {
		makeAutoObservable(this);
	}

	/** key - partyUUID */
	get marketsMap() {
		return this._marketsMap;
	}

	getMarketsList = (partyUUID: string) => toJS(this._marketsMap[partyUUID]) || [];

	getMarket = (partyUUID: string, marketUUID: string) => {
		const markets = this.getMarketsList(partyUUID);
		return markets.find((market) => market.marketUUID === marketUUID) || null;
	};

	loadData = async (partyUUID: string) => {
		if (this.getMarketsList(partyUUID).length) return;

		await this._fetchMarkets(partyUUID);
	};

	updateData = async (partyUUID: string) => {
		await this._fetchMarkets(partyUUID);
	};

	private _fetchMarkets = async (partyUUID: string) => {
		try {
			const { isError, data } = await getMarkets(partyUUID);

			if (!isError) {
				const mappedData = data.map(marketDTOMapper);
				this._setMarkets(partyUUID, mappedData);
			}
		} catch (error) {
			logError(error);
		}
	};

	private _setMarkets = (partyUUID: string, markets: MarketDataType[]) => {
		this._marketsMap[partyUUID] = markets;
	};
}
